import { Command } from "cmdk";
import {
  DatabaseBackup,
  FileText,
  FileUp,
  KeyRound,
  Link2,
  MessageCircleQuestion,
  Orbit,
  PackageSearch,
  Search,
  Waypoints,
} from "lucide-react";
import { useEffect, useImperativeHandle, useRef, useState, type Ref } from "react";
import { toast } from "sonner";

import { Kbd } from "@/components/ui/primitives";
import { useOutsidePressCapture } from "@/components/ui/use-outside-press";
import { useDocuments, useSearch } from "@/hooks/queries";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { trackJob, useUpload } from "@/hooks/use-upload";
import { api, queryClient } from "@/lib/api";
import { keys } from "@/hooks/queries";
import { cn } from "@/lib/utils";
import { NODE_COLOR } from "@/lib/palette";
import { uploadAccept } from "@/lib/upload-accept";
import { useActivity } from "@/stores/activity";
import { useGraph } from "@/stores/graph";
import { useTabs } from "@/stores/tabs";

// Heading left padding (px-1.5) + the list's own padding (p-1.5) = 12px, so the
// group heading's left edge lines up with every result row's icon (all on one
// 12px gutter — see the render below).
const GROUP_HEADING_CLASS =
  "[&_[cmdk-group-heading]]:px-1.5 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:font-mono [&_[cmdk-group-heading]]:text-2xs [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-widest [&_[cmdk-group-heading]]:text-muted-foreground";

/**
 * The atlas search — a REAL COMBOBOX, not a button that opens a modal. The
 * visible TopBar bar IS the input: you type into it and a results-only dropdown
 * (Actions / Documents / Nodes — no second input) drops directly beneath it,
 * left-aligned and at the bar's width. `cmdk` is the engine (client-side
 * filtering for Actions/Documents, debounced `/api/search` for Nodes, and
 * keyboard nav); the input and the results list live in ONE `<Command>` so
 * arrow/enter work across them.
 *
 * `controllerRef` exposes an imperative `openAndFocus()` so App's ⌘K / the
 * LeftBar "Search" open the results DETERMINISTICALLY (a direct state open +
 * focus), rather than relying on a programmatic `focus()` firing `onFocus`
 * (which is unreliable in headless and raced under load).
 *
 * Why an inline CSS-anchored dropdown and not a Base UI Popover: a popover
 * portals the list out and its focus manager (initial-focus / close-on-focus-
 * out) fights a combobox whose input must STAY focused while the list floats.
 * Anchoring the obsidian panel as an absolute child of the bar keeps focus in
 * the input, keeps cmdk's DOM (input + list in one subtree), and makes the
 * drop position pixel-exact by construction: `top = bar.bottom + 6`, `left =
 * bar.left`, `width = bar.width`. Rendered once per mounted TopBar (desktop or
 * compact), so it anchors to whichever search bar is on screen.
 */
/** Imperative handle App uses to open the results (⌘K / LeftBar "Search"). */
export type AtlasSearchHandle = { openAndFocus: () => void };

export function CommandPalette({
  controllerRef,
  barClassName,
  placeholder = "Search the atlas, open documents, run actions…",
  onAddWebsite,
  onSmartExport,
  onManageTokens,
  onManageBackups,
}: {
  /** Shared up to App so ⌘K / the LeftBar "Search" can open + focus the bar. */
  controllerRef?: Ref<AtlasSearchHandle>;
  /** Per-layout bar chrome (height / width / surface) — the desktop and compact
   * TopBars style their bar differently; behavior and the dropdown are shared. */
  barClassName?: string;
  placeholder?: string;
  onAddWebsite: () => void;
  onSmartExport: () => void;
  onManageTokens: () => void;
  onManageBackups: () => void;
}) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const docs = useDocuments();
  // Debounce the query that hits the API: the input stays instant, but only the
  // settled term fires /api/search (was one full request — and a full backend
  // embedding + cosine scan — per keystroke).
  const search = useSearch(useDebouncedValue(q, 250));
  const upload = useUpload();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const innerInputRef = useRef<HTMLInputElement | null>(null);
  const barRef = useRef<HTMLDivElement | null>(null);

  // Dismiss on an outside pointer press (the bar div contains BOTH the input
  // and the dropdown, so presses on either are excluded). This is the app's
  // tested, capture-phase, touch-safe close — and, unlike a blur handler, it
  // never fires on a spurious focus loss (which headless focus can trigger),
  // so the results don't flicker shut right after ⌘K opens them.
  useOutsidePressCapture(
    () => barRef.current,
    () => barRef.current,
    () => setOpen(false),
    open,
  );

  // Deterministic open for ⌘K / the LeftBar: set open state directly (not via a
  // focus event), then focus the input. `useImperativeHandle` runs as a layout
  // effect, so after App's `flushSync` expands the top bar this handle is ready.
  useImperativeHandle(
    controllerRef,
    () => ({
      openAndFocus: () => {
        setOpen(true);
        innerInputRef.current?.focus();
      },
    }),
    [],
  );

  // Closing the dropdown always clears the query, so the bar returns to its
  // placeholder rather than stranding a stale search term.
  useEffect(() => {
    if (!open) setQ("");
  }, [open]);

  const run = (fn: () => void) => {
    setOpen(false);
    innerInputRef.current?.blur();
    fn();
  };

  const recluster = async () => {
    const { job_id } = await api.request<{ job_id: string }>("POST", "/api/graph/recluster");
    useActivity.getState().upsert({ id: job_id, kind: "recluster", title: "recluster atlas", status: "queued" });
    toast("Reclustering the atlas…");
    const job = await trackJob(job_id);
    if (job.status === "done") {
      await queryClient.invalidateQueries({ queryKey: keys.overview });
      const checkpoint = (job.result as { checkpoint_id?: string } | null)?.checkpoint_id;
      toast.success(
        "Atlas reclustered",
        checkpoint
          ? {
              duration: 12_000,
              action: {
                label: "Undo",
                onClick: () =>
                  void api
                    .request("POST", `/api/graph/history/${checkpoint}/restore`)
                    .then(() => {
                      toast("Recluster undone");
                      void queryClient.invalidateQueries({ queryKey: keys.overview });
                    })
                    .catch((e) => toast.error("Undo failed", { description: String(e) })),
              },
            }
          : undefined,
      );
    } else {
      toast.error("Recluster failed", { description: job.error ?? "" });
    }
  };

  // Fixed action list, described declaratively so — like documents and node
  // hits below — it can be narrowed by the query instead of just re-ordered.
  const actions = [
    { id: "add-doc", label: "Add document to the atlas", icon: FileUp, action: () => fileRef.current?.click() },
    { id: "add-website", label: "Add website link to the atlas", icon: Link2, action: onAddWebsite },
    { id: "ask", label: "Ask the whole atlas", icon: MessageCircleQuestion, action: () => useTabs.getState().openAsk() },
    { id: "recluster", label: "Recluster the atlas", icon: Orbit, action: () => void recluster() },
    { id: "export", label: "Export…", icon: PackageSearch, action: onSmartExport },
    { id: "tokens", label: "Manage access tokens (MCP / CLI)…", icon: KeyRound, action: onManageTokens },
    { id: "backups", label: "Backups & cloud sync folder…", icon: DatabaseBackup, action: onManageBackups },
    { id: "goto-graph", label: "Go to the atlas", icon: Waypoints, action: () => useTabs.getState().activate("graph") },
  ];

  // cmdk's own `shouldFilter` stays off (below) — the "Nodes" group is already
  // filtered server-side (semantic search, not a substring match), so mixing
  // it with cmdk's local text-match filter would fight itself. Actions and
  // Documents are plain client-side data, so the whole palette narrows
  // consistently by filtering all three groups by the query ourselves (same
  // plain substring match ReaderTab's in-doc search already uses).
  const needle = q.trim().toLowerCase();
  const filteredActions = needle ? actions.filter((a) => a.label.toLowerCase().includes(needle)) : actions;
  const filteredDocs = (needle ? docs.data?.filter((d) => d.title.toLowerCase().includes(needle)) : docs.data) ?? [];

  return (
    <>
      {/* One <Command> wraps BOTH the inline bar-input and the (unportaled)
          results dropdown, so cmdk keyboard nav spans them. `display:contents`
          lets the bar div be the TopBar's flex child directly. */}
      <Command
        label="Search the atlas"
        shouldFilter={false} // filtering is done ourselves, above, across all three groups
        className="contents"
        onKeyDown={(e) => {
          // Esc closes the dropdown but keeps the bar (so you can keep typing);
          // cmdk itself ignores Esc, so this doesn't fight its arrow/enter nav.
          if (e.key === "Escape" && open) {
            e.preventDefault();
            setOpen(false);
          }
        }}
      >
        <div
          ref={barRef}
          className={cn(
            "relative flex items-center rounded-md border border-border text-muted-foreground transition-colors focus-within:text-foreground",
            barClassName,
          )}
        >
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          {/* pl-10 insets the text/placeholder clear of the leading icon; pr-14
              leaves room for the ⌘K hint so long placeholders never collide. */}
          <Command.Input
            ref={innerInputRef}
            id="atlas-search"
            name="atlas-search"
            autoComplete="off"
            value={q}
            onValueChange={(v) => {
              setQ(v);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            placeholder={placeholder}
            className="h-full w-full bg-transparent pl-10 pr-14 text-[13px] leading-none text-foreground placeholder:text-muted-foreground focus:outline-none"
          />
          <span className="pointer-events-none absolute right-2.5 top-1/2 hidden -translate-y-1/2 items-center gap-0.5 sm:flex">
            <Kbd>⌘</Kbd>
            <Kbd>K</Kbd>
          </span>

          {open && (
            // Anchored, results-only dropdown: top = bar.bottom + 6, left =
            // bar.left, width = bar.width — no positioning library needed. z-50
            // escapes above the (relative) workspace since the bar creates no
            // stacking context of its own.
            <div
              // Keep focus in the input when interacting with the dropdown, so
              // the onBlur-close only ever fires for genuine OUTSIDE clicks.
              // Result rows aren't focusable, but this also covers the footer /
              // empty area and hardens against browser quirks.
              onMouseDown={(e) => e.preventDefault()}
              // `-left-px` + `w-[calc(100%+2px)]` make the panel's border-box
              // line up with the bar's border-box exactly (absolute children are
              // laid out against the padding box, so plain `left-0`/`w-full`
              // would sit 1px inside the border on each edge). top = bar bottom + 6.
              className="absolute -left-px top-[calc(100%+6px)] z-50 w-[calc(100%+2px)] origin-top overflow-hidden panel shadow-deep animate-rise-in"
            >
              {/* Touch scroll on tablets: `overscroll-contain` stops the scroll
                  chaining to the body (bounce / pull-to-refresh that reads as a
                  "trap"); `touch-action: pan-y` + momentum let a finger drag the
                  list. Nothing here preventDefaults touchmove. */}
              <Command.List className="max-h-[46vh] overflow-y-auto overscroll-contain p-1.5 [-webkit-overflow-scrolling:touch] [touch-action:pan-y]">
                <Command.Empty className="px-3 py-6 text-center text-xs text-muted-foreground">
                  Nothing found in the atlas.
                </Command.Empty>

                {filteredActions.length > 0 && (
                  <Command.Group heading="Actions" className={GROUP_HEADING_CLASS}>
                    {filteredActions.map((a) => (
                      <Item key={a.id} icon={a.icon} onSelect={() => run(a.action)}>
                        {a.label}
                      </Item>
                    ))}
                  </Command.Group>
                )}

                {filteredDocs.length > 0 && (
                  <Command.Group heading="Documents" className={GROUP_HEADING_CLASS}>
                    {filteredDocs.slice(0, 8).map((d) => (
                      <Item
                        key={d.id}
                        icon={FileText}
                        onSelect={() => run(() => useTabs.getState().openReader(d.id, d.title))}
                      >
                        {d.title}
                        <span className="ml-auto font-mono text-2xs text-muted-foreground">{d.status}</span>
                      </Item>
                    ))}
                  </Command.Group>
                )}

                {(search.data?.hits.length ?? 0) > 0 && (
                  <Command.Group heading="Nodes" className={GROUP_HEADING_CLASS}>
                    {search.data!.hits.map((h) => (
                      <Item
                        key={h.node.id}
                        value={`node-${h.node.id}`}
                        onSelect={() =>
                          run(() => {
                            useTabs.getState().activate("graph");
                            useGraph.getState().requestFocus(h.node.id);
                          })
                        }
                      >
                        {/* Dot boxed to the same w-4 icon column so node labels
                            align with the icon'd Action/Document rows. */}
                        <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                          <span className="h-2 w-2 rounded-full" style={{ background: NODE_COLOR[h.node.type] }} />
                        </span>
                        <span className="truncate">{h.node.label}</span>
                        <span className="ml-auto font-mono text-2xs text-muted-foreground">{h.node.type}</span>
                      </Item>
                    ))}
                  </Command.Group>
                )}
              </Command.List>
              {/* Footer hints share the same 12px left gutter as the result rows. */}
              <div className="flex items-center gap-4 border-t border-border px-3 py-2 font-mono text-[10px] text-muted-foreground">
                <span>↑↓ navigate</span>
                <span>↵ open</span>
                <span>esc close</span>
              </div>
            </div>
          )}
        </div>
      </Command>
      <input
        ref={fileRef}
        type="file"
        multiple
        accept={uploadAccept()}
        className="hidden"
        onChange={(e) => {
          if (e.target.files) void upload(e.target.files);
          e.target.value = "";
        }}
      />
    </>
  );
}

function Item({
  children,
  icon: Icon,
  onSelect,
  value,
}: {
  children: React.ReactNode;
  icon?: React.ComponentType<{ className?: string }>;
  onSelect: () => void;
  value?: string;
}) {
  return (
    <Command.Item
      value={value}
      onSelect={onSelect}
      // px-1.5 + the list's p-1.5 = 12px, so the icon column lines up with the
      // group headings; gap-2.5 keeps every label on one text column.
      className="flex cursor-pointer items-center gap-2.5 rounded-md px-1.5 py-2 text-[13px] text-foreground data-[selected=true]:bg-accent"
    >
      {Icon && <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />}
      {children}
    </Command.Item>
  );
}
